/// <amd-module name="Wiki/Renderer/WikiMarkdownRenderer" />
import * as React from "react";
import { MarkdownRenderer } from "VSS/Features/Markdown/MarkdownRenderer";
import { MarkdownRendererComponent } from "VSS/Features/Markdown/MarkdownRendererComponent";
import { ContainerOptions, LineAttrInfoMap, MarkdownRendererOptions } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { AnchoredHeaders } from "VSS/Features/Markdown/Plugins/AnchoredHeaders";
import { ISubPage } from "VSS/Features/Markdown/Plugins/TableOfSubPagesPlugin";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";
import { css, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Uri } from "VSS/Platform/Util/Url";
import { WikiPage, WikiType } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { focusAnchor, isBrokenLink, isInternalAnchorLink, isUrlExternalToWiki, scrollToAnchor, wikiLinkOnClickEventHelper } from "Wiki/Renderer/Helpers";
import { ImageTransformer } from "Wiki/Renderer/ImageTransformer";
import { LinkTransformer } from "Wiki/Renderer/LinkTransformer";
import { MarkdownSyntaxExtensionProvider, PreviewMode as MdSyntaxExtensionProviderPreviewMode } from "Wiki/Renderer/MarkdownSyntaxExtensionProvider";
import { MentionSyntaxProcessor } from "Wiki/Renderer/MentionSyntaxProcessor";
import { TemplateProcessor } from "Wiki/Renderer/TemplateProcessor";
import { IWikiMarkdownRendererProps } from "Wiki/Renderer/WikiMarkdownRenderer.Props";
import { RepoConstants, UrlConstants } from "Wiki/ViewCommon/Constants";
import { Areas, TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getWikiTypeString } from "Wiki/ViewCommon/Utils/WikiHelper";

export const enum ClassNamesConstants {
    WikiMdContainer = "wiki-md-container"
}

export namespace PreviewMode {
    export const Off = 1;
    export const On = 2;
}

export class WikiMarkdownRenderer extends VssComponent<IWikiMarkdownRendererProps> {
    private _imageTransformer: ImageTransformer;
    private _linkTransformer: LinkTransformer;
    private _containerOptions: ContainerOptions[];
    private _markdownContainerRef: React.RefObject<HTMLDivElement>;
    private _markdownRendererComponentRef = React.createRef<MarkdownRendererComponent>();
    private _mentionSyntaxProcessor: MentionSyntaxProcessor;
    private _markdownSyntaxExtensionProvider: MarkdownSyntaxExtensionProvider;
    private _templateProcessor: TemplateProcessor;
    private _sourceReplacedWithTemplateContent: string | undefined = undefined;
    private _mentionsModulesPromise: Promise<any[]>;
    private _isCreateWitAllowed: boolean;
    private _markdownLineMapping: LineAttrInfoMap;
    private _shouldHonorLineBreak: boolean;

    constructor(props: IWikiMarkdownRendererProps, context: IVssComponentContext) {
        super(props, context);

        let wikiRootPath: string = RepoConstants.RootPath;
        this._shouldHonorLineBreak = true;

        const wiki = props.wiki;
        if (wiki) {
            wikiRootPath = wiki.mappedPath;
            this._shouldHonorLineBreak = WikiType.ProjectWiki === wiki.type;
        }

        this._imageTransformer = new ImageTransformer(
            this.context.pageContext,
            this.props.projectId,
            this.props.repositoryId,
            this.props.wikiVersion,
            wikiRootPath
        );
        this._linkTransformer = new LinkTransformer(
            this.context.pageContext,
            this.props.projectId,
            this.props.repositoryId,
            wikiRootPath,
            this.props.isHostedOutsideWikiHub,
            this.props.wikiIdentifier || (this.props.wiki && this.props.wiki.name),
            this.props.wikiVersion
        );
        this._mentionSyntaxProcessor = new MentionSyntaxProcessor(this.context.pageContext);

        this._containerOptions = [];

        if (isFeatureFlagEnabled(this.context.pageContext, "WebAccess.Wiki.TemplateSupport", false)) {
            this._templateProcessor = new TemplateProcessor(this.context.pageContext, {
                _updateContent: this._onUpdateContent,
                createMarkdownRenderer: this._createMarkdownRendererForTemplates,
                getProps: () => this.props
            });
            this._containerOptions.push(this._templateProcessor);
        }
        this._markdownSyntaxExtensionProvider = new MarkdownSyntaxExtensionProvider(
            this.context.pageContext,
            { getViewMode: this._getViewMode },
            "." + ClassNamesConstants.WikiMdContainer
        );
        this._markdownSyntaxExtensionProvider.fetchMarkdownSyntaxContributors("ms.vss-wiki-web.wiki-markdown-extension-syntax");
        this._containerOptions.push(this._markdownSyntaxExtensionProvider);
        this._isCreateWitAllowed =
            this.props.handleEmbedWorkItem !== undefined && isFeatureFlagEnabled(this.context.pageContext, "Wiki.SelectAndCreateWorkItems", false);

        this._markdownContainerRef = props.markdownContainerRef ? props.markdownContainerRef : React.createRef<HTMLDivElement>();
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this._scrollToAnchorHandler(true);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: IWikiMarkdownRendererProps): void {
        if (this._templateProcessor && this.props.content !== nextProps.content) {
            this._sourceReplacedWithTemplateContent = this._templateProcessor.fetchUpdatedMarkdown(nextProps.content, nextProps.pagePath);
        }
    }

    public componentDidUpdate(prevProps: IWikiMarkdownRendererProps, prevState: {}): void {
        super.componentDidUpdate(prevProps, prevState);

        if (this.props.anchor !== prevProps.anchor) {
            this._scrollToAnchorHandler();
        }
    }

    public render(): JSX.Element {
        const { className, content, onInitialRender, wiki, getWikiPage } = this.props;
        return (
            <>
                <div className={css(ClassNamesConstants.WikiMdContainer, className)} ref={this._markdownContainerRef}>
                    <MarkdownRendererComponent
                        ref={this._markdownRendererComponentRef}
                        className={"markdown-render-area body-m"}
                        customSyntaxProcessor={this._mentionSyntaxProcessor}
                        rawContent={this._sourceReplacedWithTemplateContent || content}
                        linkClickHandler={this._linkClickHandler}
                        previewMode={this.props.viewMode}
                        options={{
                            breaks: this._shouldHonorLineBreak,
                            containerOptions: this._containerOptions,
                            html: true,
                            enableHeaderAnchorSharing: true,
                            enableYaml: true,
                            imageUrlTransformer: this._transformImageUrl,
                            linkTransformer: this._transformLinkUrl,
                            linkCustomStyleAppender: getWikiPage ? this._transformLinkStyle : undefined,
                            enableMarkdownLinesMapping: this._isCreateWitAllowed || this.props.isSyncScrollEnabled,
                            onMarkdownLinesMappingComplete:
                                this._isCreateWitAllowed || this.props.isSyncScrollEnabled ? this._onMarkdownLinesMappingComplete : undefined,
                            subPages: this._getSubPages()
                        }}
                        onInitialRender={onInitialRender}
                        injectExtensionMarkup={this._injectExtensionMarkup}
                    />
                </div>
                {this._isCreateWitAllowed && (
                    <WrappedComponent
                        wrappedType={"Wiki.CreateWitMenu"}
                        dependencies={["ms.vss-wiki-web.wiki-create-wit-content", "ms.vss-work-web.new-workitem-data-provider"]}
                        markdownContent={this.props.content}
                        getMarkdownLinesMapping={() => this._markdownLineMapping}
                        handleEmbedWorkItem={this.props.handleEmbedWorkItem}
                        handleEmbedWorkItemFailure={this.props.handleEmbedWorkItemFailure}
                        hasTemplates={() => this._sourceReplacedWithTemplateContent && this._sourceReplacedWithTemplateContent !== this.props.content}
                    />
                )}
            </>
        );
    }

    public getRenderedContent(): string | undefined {
        return this._markdownRendererComponentRef.current ? this._markdownRendererComponentRef.current.getRenderedContent() : undefined;
    }

    private _getSubPages = (): ISubPage[] | undefined => {
        if (!isFeatureFlagEnabled(this.context.pageContext, "Wiki.TableOfSubPages", false) || !this.props.wikiPages) return undefined;
        const page = this.props.wikiPages.find(value => value.path == this.props.pagePath);
        if (page) return this._toISubPages(page.subPages)
        return [];
    }

    private _toISubPages = (subPages: WikiPage[]): ISubPage[] =>
        subPages
            .sort((currentPage, nextPage) => currentPage.order - nextPage.order)
            .map(value => {
                return {
                    name: this._getPageNameFromPath(value.path),
                    remoteUrl: value.remoteUrl,
                    subPages: this._toISubPages(value.subPages)
                }
            });

    private _getPageNameFromPath = (path: string): string => {
        const match = path.match(/\/([^\/]*)$/);
        let result = path;
        if (match != null) {
            result = match.pop() || result;
        }
        return result;
    }

    private _onMarkdownLinesMappingComplete = (mapping: LineAttrInfoMap): void => {
        this._markdownLineMapping = mapping;
        if (this.props.onMarkdownLinesMappingChanged && this.props.isSyncScrollEnabled) {
            this.props.onMarkdownLinesMappingChanged(mapping);
        }
    };

    private _onUpdateContent = (updatedContent: string): void => {
        this._sourceReplacedWithTemplateContent = updatedContent;
        // calling forceUpdate() causes render() to be called on the component, skipping shouldComponentUpdate()
        // here skipping shouldComponentUpdate() check causes no harm as this flow only gets triggered when a new template is downloaded
        // and component's output is affected by the current changes in props
        this.forceUpdate();
    };

    private _scrollToAnchorHandler = (scrollOnlyIfAnchorIsDefined?: boolean): void => {
        let { anchor } = this.props;

        if (!anchor) {
            const href = window.location.href;
            const indexOfAnchorSign = href.indexOf("#");

            if (indexOfAnchorSign > -1) {
                anchor = href.substring(indexOfAnchorSign + 1, href.length);
            }
        }

        if (anchor) {
            scrollToAnchor(anchor);
            focusAnchor(anchor);
        } else if (!scrollOnlyIfAnchorIsDefined && this.props.scrollToElementClassName) {
            const elements = document.getElementsByClassName(this.props.scrollToElementClassName);
            if (elements.length > 0) {
                elements[0].scrollTop = 0;
            }
        }
    };

    private _getViewMode = (): number => {
        return !this.props.viewMode || this.props.viewMode === PreviewMode.Off
            ? MdSyntaxExtensionProviderPreviewMode.Off
            : MdSyntaxExtensionProviderPreviewMode.On;
    };

    private _injectExtensionMarkup = (): void => {
        this._markdownSyntaxExtensionProvider.createContributionsHosts();

        this._getMentionsModulesPromise().then(([PeopleMentions, WitMentions]) => {
            const telemetryProperties = {
                SourcePageArea: Areas.Wiki,
                wikiType: getWikiTypeString(this.props.wiki ? this.props.wiki.type : WikiType.ProjectWiki),
                [TelemetryConstants.NewWiki]: true
            };

            // register mention click handlers to log the telemetry.
            PeopleMentions.PeopleMentionsRenderingProvider.registerMentionClickHandler(this._markdownContainerRef.current, telemetryProperties);
            WitMentions.WorkItemMentionsRenderingProvider.registerMentionClickHandler(
                this._markdownContainerRef.current,
                telemetryProperties,
                (href: string, event: React.SyntheticEvent<HTMLElement, Event>) => onClickFPS(this.context.pageContext, href, true, event)
            );
        });

        // to handle scroll into view after delay renders in read mode.
        if (this._getViewMode() == MdSyntaxExtensionProviderPreviewMode.Off) {
            this._scrollToAnchorHandler(true);
        }
    };

    private _getMentionsModulesPromise = (): Promise<any[]> => {
        if (!this._mentionsModulesPromise) {
            const legacyPlatformService = this.context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
            this._mentionsModulesPromise = this.trackPromise(
                legacyPlatformService.requireModules(["Mention/Scripts/TFS.Mention.People", "Mention/Scripts/TFS.Mention.WorkItems"])
            ).promise;
        }

        return this._mentionsModulesPromise;
    };

    private _linkClickHandler = (event: MouseEvent): boolean => {
        const anchorElement = event.currentTarget as HTMLAnchorElement;
        const hrefAttr = anchorElement && anchorElement.attributes && anchorElement.attributes.getNamedItem("href");
        const href = hrefAttr ? hrefAttr.value : undefined;
        if (href && isInternalAnchorLink(href, this.context.pageContext, this.props.wiki, this.props.pagePath, this.props.pageId)) {
            try {
                const isOnlyAnchor = href.indexOf("#") === 0;
                const uri = new Uri(href);
                const anchor = isOnlyAnchor
                    ? decodeURIComponent(href.substring(1)).toLowerCase()
                    : uri.getQueryParam(UrlConstants.AnchorParam) || undefined;

                if (this.props.skipInternalLinkTransformation) {
                    anchorElement.href = "#" + anchor;
                }

                if (anchor) {
                    if (anchor === this.props.anchor) {
                        // User has clicked on anchor again. URL will not be updated. Just need to scroll to anchor.
                        this._scrollToAnchorHandler();
                    } else {
                        // Update URL and scroll to anchor.
                        if (this.props.onAnchorClick) {
                            this.props.onAnchorClick(anchor);
                        }
                    }
                    return false;
                }
            } catch (e) {
                /*
                 * Uri.parse or decodeURIComponent can throw exception if URL is malformed
                 * Consider it as an external URL
                 */
            }
        }

        return _handleWikiLinkClick(this.context.pageContext, event as any);
    };

    private _transformImageUrl = (url: string): string => {
        return this._imageTransformer.transformImage(url, this.props.unsavedAttachmentsMap, this.props.pagePath);
    };

    private _transformLinkUrl = (href: string): string => {
        return this._linkTransformer.transformLink(href, this.props.pagePath, this.props.getWikiPage);
    };

    private _transformLinkStyle = (href: string, className?: string): string[] => {
        if (this.props.getWikiPage) {
            const currentUrl = _getCurrentUrl(this.context.pageContext);
            return _transformLinkStyle(href, this.props.getWikiPage, currentUrl, className);
        } else {
            return [];
        }
    };

    private _createMarkdownRendererForTemplates = (): MarkdownRenderer => {
        const options: MarkdownRendererOptions = {
            containerOptions: this._containerOptions
        };
        return new MarkdownRenderer(this.context.pageContext, options);
    };
}

/**
 * Function to enable FPS navigation for wiki page link click
 * @param event - event object
 * @returns true for external links to navigate by default; false for wiki page links to stop default navigation
 */
// Exported for testing
export function _handleWikiLinkClick(pageContext: IVssPageContext, event: React.MouseEvent<HTMLAnchorElement>): boolean {
    const href = event.currentTarget.href;
    const currentUrl = _getCurrentUrl(pageContext);
    if (href && isUrlExternalToWiki(href, currentUrl)) {
        return true;
    }

    return wikiLinkOnClickEventHelper(event, () => onClickFPS(pageContext, href, true, event));
}

// Exported for testing
export function _transformLinkStyle(
    href: string,
    getWikiPage: (pagePath: string) => WikiPage | undefined,
    currentUrl: string,
    className?: string
): string[] {
    // Ignore shareHeaderAnchor links
    if (className && className.split(" ").indexOf(AnchoredHeaders.shareHeaderAnchorClassName) >= 0) {
        return [];
    }

    if (isBrokenLink(href, getWikiPage, currentUrl)) {
        return ["wiki-broken-link"];
    }

    return [];
}

// Exported for testing
export function _getCurrentUrl(pageContext: IVssPageContext): string {
    const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
    return historyService.generateUrl(historyService.getCurrentRouteValues(), StateMergeOptions.currentState);
}

VssComponent.register("TFS.WikiMarkdownRenderer", WikiMarkdownRenderer);
